// SocialCardsTab.jsx — Social Cards tab panel, extracted from LimitedPartnerPortal.
// Renders the nine 1080×1080 shareable-card canvases, draws them on mount /
// data change (the parent only mounts this component while the tab is active),
// and downloads a PNG on click. Canvas renderers live in ../socialCards.js.
import React, { useEffect } from "react";
import {
  drawWelcomeCard,
  drawBadgeCard,
  drawChapterStatsCard,
  drawRecruitmentCard,
  drawApprovalDialogCard,
  drawApplicationFormCard,
  drawInviteCard,
  drawCheckCard,
  drawDesktopCard,
} from "../socialCards";

/**
 * Local helper for the Social Cards tab. Wraps the 1080×1080 preview canvas
 * in a sunken chrome well, with eyebrow, title, and a bevelled chrome
 * "Download" button below. Clicking either the canvas preview or the button
 * triggers the download callback passed in.
 */
function SocialCardTile({ canvasId, title, eyebrow, description, onDownload }) {
  return (
    <article className="social-card-tile">
      <div
        className="social-card-tile__preview"
        onClick={onDownload}
        style={{
          borderColor: 'var(--bevel-sunken)',
          boxShadow: 'var(--shadow-sunken)',
          transform: 'none',
          transition: 'none',
        }}
      >
        <canvas
          id={canvasId}
          width="1080"
          height="1080"
          style={{ display: 'block', width: '100%', height: 'auto', cursor: 'pointer' }}
          aria-label={`${title} preview. Click to download`}
        />
      </div>
      <div className="social-card-tile__body">
        <div className="social-card-tile__eyebrow">{eyebrow}</div>
        <h3 className="social-card-tile__title">{title}</h3>
        <p className="social-card-tile__description">{description}</p>
        <button
          type="button"
          className="win95-btn"
          onClick={onDownload}
          style={{
            marginTop: 4,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          Download PNG
          <span aria-hidden="true">↓</span>
        </button>
      </div>
    </article>
  );
}

export default function SocialCardsTab({ user, userStats, userBadges, isAdmin, adminPitches, lpPitches }) {

const downloadCard = async (type) => {
  const canvasId =
    type === 'welcome' ? 'welcome-canvas' :
    type === 'badge' ? 'badge-canvas' :
    type === 'stats' ? 'stats-canvas' :
    type === 'recruitment' ? 'recruitment-canvas' :
    type === 'approval' ? 'approval-canvas' :
    type === 'invite' ? 'invite-canvas' :
    type === 'check' ? 'check-canvas' :
    type === 'desktop' ? 'desktop-canvas' :
    'application-canvas';
  const canvas = document.getElementById(canvasId);

  if (!canvas) return;

  if (type === 'welcome') {
    await drawWelcomeCard(canvas, user);
  } else if (type === 'badge') {
    await drawBadgeCard(canvas, user, userBadges);
  } else if (type === 'stats') {
    await drawChapterStatsCard(canvas, user, isAdmin, adminPitches, lpPitches);
  } else if (type === 'recruitment') {
    await drawRecruitmentCard(canvas, user);
  } else if (type === 'approval') {
    await drawApprovalDialogCard(canvas, user);
  } else if (type === 'application') {
    await drawApplicationFormCard(canvas, user);
  } else if (type === 'invite') {
    await drawInviteCard(canvas, user);
  } else if (type === 'check') {
    await drawCheckCard(canvas);
  } else if (type === 'desktop') {
    await drawDesktopCard(canvas, user);
  }

  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gnf-${type}-card-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  });
};

// Draw cards when the tab mounts or its data changes. The parent only renders
// this component while the Social Cards tab is active, so mounting replaces
// the old activeTab === 'socialCards' gate. Dependency list intentionally
// matches the pre-extraction effect (userStats included, lpPitches/isAdmin not).
useEffect(() => {
  setTimeout(async () => {
    const welcomeCanvas = document.getElementById('welcome-canvas');
    const badgeCanvas = document.getElementById('badge-canvas');
    const statsCanvas = document.getElementById('stats-canvas');
    const recruitmentCanvas = document.getElementById('recruitment-canvas');
    const approvalCanvas = document.getElementById('approval-canvas');
    const applicationCanvas = document.getElementById('application-canvas');
    const inviteCanvas = document.getElementById('invite-canvas');
    const checkCanvas = document.getElementById('check-canvas');
    const desktopCanvas = document.getElementById('desktop-canvas');

    if (checkCanvas) await drawCheckCard(checkCanvas);
    if (desktopCanvas) await drawDesktopCard(desktopCanvas, user);
    if (welcomeCanvas) await drawWelcomeCard(welcomeCanvas, user);
    if (badgeCanvas) await drawBadgeCard(badgeCanvas, user, userBadges);
    if (statsCanvas) await drawChapterStatsCard(statsCanvas, user, isAdmin, adminPitches, lpPitches);
    if (recruitmentCanvas) await drawRecruitmentCard(recruitmentCanvas, user);
    if (approvalCanvas) await drawApprovalDialogCard(approvalCanvas, user);
    if (applicationCanvas) await drawApplicationFormCard(applicationCanvas, user);
    if (inviteCanvas) await drawInviteCard(inviteCanvas, user);
  }, 100);
}, [user, userStats, userBadges, adminPitches]);

return (
  <div className="social-cards-page">
    {/* Pane header — chrome band replaces the old magenta hero */}
    <div className="win95-pane-header">
      <div>
        <div className="win95-pane-header__context">Share tools</div>
        <h2 className="win95-pane-header__title">Social cards</h2>
      </div>
      <div className="win95-pane-header__stats">
        <div className="win95-statpanel">
          <div className="win95-statpanel__label">Cards</div>
          <div className="win95-statpanel__figure">9</div>
        </div>
      </div>
    </div>

    <section className="admin-section admin-section--paper">
      <div className="social-cards-container">
        <SocialCardTile
          canvasId="check-canvas"
          title="The $1,000 check"
          eyebrow="Card 01"
          description="The big check, made out to “Your Business Here” and auto-dated with this quarter&rsquo;s deadline. Post it to drive 60-second pitch applications."
          onDownload={() => downloadCard('check')}
        />
        <SocialCardTile
          canvasId="invite-canvas"
          title="Join me as an LP"
          eyebrow="Card 02"
          description="A personal, color-blocked invite: your name, chapter, and the four facts that recruit the next Limited Partner."
          onDownload={() => downloadCard('invite')}
        />
        <SocialCardTile
          canvasId="welcome-canvas"
          title="Welcome to GNF"
          eyebrow="Card 03"
          description="Introduce yourself to the chapter."
          onDownload={() => downloadCard('welcome')}
        />
        <SocialCardTile
          canvasId="badge-canvas"
          title="Badge achievements"
          eyebrow="Card 04"
          description="Show off the trophies you&rsquo;ve earned."
          onDownload={() => downloadCard('badge')}
        />
        <SocialCardTile
          canvasId="stats-canvas"
          title="Chapter impact"
          eyebrow="Card 05"
          description="Your chapter&rsquo;s funded-business tally."
          onDownload={() => downloadCard('stats')}
        />
        <SocialCardTile
          canvasId="recruitment-canvas"
          title="LP recruitment"
          eyebrow="Card 06"
          description="Rally new LPs into the network."
          onDownload={() => downloadCard('recruitment')}
        />
        <SocialCardTile
          canvasId="approval-canvas"
          title="Approval dialog"
          eyebrow="Card 07"
          description="Belief, in writing: a Win95 grant-approved notification."
          onDownload={() => downloadCard('approval')}
        />
        <SocialCardTile
          canvasId="application-canvas"
          title="Application form"
          eyebrow="Card 08"
          description="Apply in one sentence: “What&rsquo;s your big idea?”"
          onDownload={() => downloadCard('application')}
        />
        <SocialCardTile
          canvasId="desktop-canvas"
          title="The desktop"
          eyebrow="Card 09"
          description="The whole NeighborhoodOS in one frame: Start menu open, Submit pitch on screen, clouds behind. For when the vibe is the pitch."
          onDownload={() => downloadCard('desktop')}
        />
      </div>
    </section>
  </div>
);
}
